/* global Word */

import type { TemplateProfile } from "../../templates/types";
import { TableBuilder } from "../builders/TableBuilder";
import { ParagraphBuilder } from "../builders/ParagraphBuilder";
import { ContentControlBuilder } from "../builders/ContentControlBuilder";
import { setExhibitHeaderText } from "./renumbering";
import { SOURCE_ROW_SPACE_BEFORE_PT } from "../../core/config/constants";

/**
 * Sets up the header row cell of an exhibit table.
 * Writes the exhibit number label and description runs.
 *
 * IMPORTANT: `cell` must be a valid, loaded reference obtained AFTER
 * the calling strategy's second rows load+sync cycle.
 */
export async function setupHeaderCell(
  context: Word.RequestContext,
  cell: Word.TableCell,
  exhibitNumber: number,
  profile: TemplateProfile,
  opts?: { leftIndent?: number }
): Promise<void> {
  TableBuilder.zeroCellPadding(cell);
  cell.verticalAlignment = Word.VerticalAlignment.top;

  // Load this cell's paragraphs before accessing them
  cell.body.paragraphs.load("items");
  await context.sync();

  const paras = cell.body.paragraphs.items;
  if (paras.length > 1) {
    for (let i = 1; i < paras.length; i++) {
      paras[i].delete();
    }
  }

  const para = paras[0];
  ParagraphBuilder.clearSpacing(para);
  para.alignment   = Word.Alignment.justified;
  para.leftIndent  = opts?.leftIndent ?? 0;
  para.rightIndent = 0;

  setExhibitHeaderText(para, exhibitNumber, profile);
  await context.sync();
}

/**
 * Sets up the header row cell of a first-page exhibit table.
 * Writes only the description run without the "Exhibit N:" prefix.
 */
export async function setupTitleOnlyHeaderCell(
  context: Word.RequestContext,
  cell: Word.TableCell,
  profile: TemplateProfile,
  opts?: { leftIndent?: number }
): Promise<void> {
  TableBuilder.zeroCellPadding(cell);
  cell.verticalAlignment = Word.VerticalAlignment.top;

  // Load this cell's paragraphs before accessing them
  cell.body.paragraphs.load("items");
  await context.sync();

  const paras = cell.body.paragraphs.items;
  if (paras.length > 1) {
    for (let i = 1; i < paras.length; i++) {
      paras[i].delete();
    }
  }

  const para = paras[0];
  ParagraphBuilder.clearSpacing(para);
  para.alignment   = Word.Alignment.justified;
  para.leftIndent  = opts?.leftIndent ?? 0;
  para.rightIndent = 0;

  ParagraphBuilder.insertTextRun(para, "Title or description here", {
    fontFamily: profile.typography.fontFamily,
    fontSize:   profile.typography.headerFontSize,
    bold:       true,
    color:      profile.colorPalette.exhibitDesc,
  });

  await context.sync();
}

/**
 * Sets up the graph row cell — clears the paragraph and inserts
 * a hidden rich-text content control as the graph placeholder.
 */
export async function setupGraphCell(
  context: Word.RequestContext,
  cell: Word.TableCell,
  dimensions?: { height: number; width: number }
): Promise<void> {
  TableBuilder.zeroCellPadding(cell);
  cell.verticalAlignment = Word.VerticalAlignment.top;

  cell.body.paragraphs.load("items");
  await context.sync();

  const paras = cell.body.paragraphs.items;
  if (paras.length > 1) {
    for (let i = 1; i < paras.length; i++) {
      paras[i].delete();
    }
  }

  const para = paras[0];
  para.clear();
  para.alignment = Word.Alignment.left;

  let placeholderText = 'Paste your graph here';
  if (dimensions) {
    const h = (dimensions.height / 28.3465).toFixed(2);
    const w = (dimensions.width / 28.3465).toFixed(2);
    placeholderText = `Paste your graph here (${h}*${w} cm)`;
  }

  ContentControlBuilder.insertGraphPlaceholder(para, placeholderText);
  await context.sync();
}

/**
 * Sets up the cell for a table paste area (drop zone).
 * Inserts a content control placeholder for the table/image.
 */
export async function setupTablePasteCell(
  context: Word.RequestContext,
  cell: Word.TableCell,
  dimensions?: { height: number; width: number }
): Promise<void> {
  TableBuilder.zeroCellPadding(cell);
  cell.verticalAlignment = Word.VerticalAlignment.top;
  cell.shadingColor = '#ffffff';

  cell.body.paragraphs.load("items");
  await context.sync();

  const paras = cell.body.paragraphs.items;
  if (paras.length > 1) {
    for (let i = 1; i < paras.length; i++) {
      paras[i].delete();
    }
  }

  const para = paras[0];
  para.clear();
  para.alignment = Word.Alignment.left;

  let placeholderText = 'Paste your Table/Image here';
  if (dimensions) {
    const h = (dimensions.height / 28.3465).toFixed(2);
    const w = (dimensions.width / 28.3465).toFixed(2);
    placeholderText = `Paste your Table/Image here (${h}*${w} cm)`;
  }

  ContentControlBuilder.insertTablePlaceholder(para, placeholderText);
  await context.sync();
}

/**
 * Sets up the source row cell — inserts placeholder source text
 * styled with the profile's typography and color palette.
 */
export async function setupSourceCell(
  context: Word.RequestContext,
  cell: Word.TableCell,
  profile: TemplateProfile,
  opts?: { leftIndent?: number }
): Promise<void> {
  TableBuilder.zeroCellPadding(cell);
  cell.verticalAlignment = Word.VerticalAlignment.center;

  cell.body.paragraphs.load("items");
  await context.sync();

  const paras = cell.body.paragraphs.items;
  if (paras.length > 1) {
    for (let i = 1; i < paras.length; i++) {
      paras[i].delete();
    }
  }

  const para = paras[0];
  ParagraphBuilder.clearSpacing(para);
  para.spaceBefore = SOURCE_ROW_SPACE_BEFORE_PT;
  para.alignment   = Word.Alignment.justified;
  para.leftIndent  = opts?.leftIndent ?? 0;
  para.rightIndent = 0;

  ParagraphBuilder.insertTextRun(para, "Source: source text here", {
    fontFamily: profile.typography.fontFamily,
    fontSize:   profile.typography.sourceFontSize,
    bold:       false,
    color:      profile.colorPalette.sourceText,
  });

  await context.sync();
}